/**
 * Vuex 模块：历史路由 + 分页缓存管理
 * 功能：
 * 1. 管理历史路由（不限制数量）
 * 2. 缓存分页查询条件，包括：
 *    - dateRange（日期选择）
 *    - userId（用户查询）
 *    - role（角色查询）
 *    - selectdata（内容模糊查询）
 * 3. 支持刷新页面或返回页面时自动恢复缓存
 */

export default {
  namespaced: true,
  state: {
    historyRoutes: [], // 存储历史路由记录
    pageCache: {} // 存储分页缓存，key = 页面唯一标识
  },
  mutations: {
    /**
     * 添加历史路由
     * @param state
     * @param route { path, title }
     */
    ADD_ROUTE(state, route) {
      // 首页不添加到历史记录
      if (route.path === '/welcome' || route.path === '/home' || route.path === '/') {
        return
      }

      // 检查是否已存在相同路径的记录
      const existingIndex = state.historyRoutes.findIndex(r => r.path === route.path)

      if (existingIndex !== -1) {
        // 存在则替换原来的记录（更新标题）
        state.historyRoutes[existingIndex] = route
      } else {
        // 不存在就添加到末尾
        state.historyRoutes.push(route)
      }
    },

    /**
     * 删除历史路由
     */
    REMOVE_ROUTE(state, route) {
      state.historyRoutes = state.historyRoutes.filter(r => r.path !== route.path)
    },

    /**
     * 清空历史路由
     */
    CLEAR_HISTORY(state) {
      state.historyRoutes = []
    },

    /**
     * 保存分页缓存
     * @param key 页面唯一标识，如 pmPageCache
     * @param params 分页查询参数
     */
    SET_PAGE_CACHE(state, { key, params }) {
      // 深拷贝，防止引用导致的意外修改
      state.pageCache[key] = JSON.parse(JSON.stringify(params))
      sessionStorage.setItem(key, JSON.stringify(params))
    },

    /**
     * 删除指定页面缓存
     * @param key 页面缓存key
     */
    REMOVE_PAGE_CACHE(state, key) {
      if (state.pageCache && state.pageCache[key]) {
        delete state.pageCache[key]
        sessionStorage.removeItem(key)
      }
    },

    /**
     * 清除所有页面缓存
     */
    CLEAR_ALL_PAGE_CACHE(state) {
      // 清除 sessionStorage 中的所有缓存
      const keys = Object.keys(state.pageCache)
      keys.forEach(key => {
        sessionStorage.removeItem(key)
      })
      // 清空 state
      state.pageCache = {}
    }
  },
  actions: {
    addRoute({ commit }, route) {
      commit('ADD_ROUTE', route)
    },

    removeRoute({ commit }, route) {
      commit('REMOVE_ROUTE', route)
    },

    clearHistory({ commit }) {
      commit('CLEAR_HISTORY')
    },

    // 保存分页缓存
    savePageCache({ commit }, { key, params }) {
      commit('SET_PAGE_CACHE', { key, params })
    },

    // 恢复分页缓存
    async restorePageCache({ state }, { key, defaultParams }) {
      try {
        const cacheStr = sessionStorage.getItem(key)
        if (cacheStr) {
          const params = JSON.parse(cacheStr)
          const dateRange = (params.query && params.query.dateRange) ? params.query.dateRange : null
          const userId = (params.query && params.query.userId) ? params.query.userId : ''
          const role = (params.query && params.query.role) ? params.query.role : ''
          const selectdata = (params.query && params.query.selectdata) ? params.query.selectdata : ''
          state.pageCache[key] = params
          return { params, dateRange, userId, role, selectdata }
        }
      } catch (e) {
        console.error('缓存解析失败', e)
      }

      // 没有缓存时返回默认值
      state.pageCache[key] = defaultParams
      return {
        params: defaultParams,
        dateRange: (defaultParams.query && defaultParams.query.dateRange) ? defaultParams.query.dateRange : null,
        userId: (defaultParams.query && defaultParams.query.userId) ? defaultParams.query.userId : '',
        role: (defaultParams.query && defaultParams.query.role) ? defaultParams.query.role : '',
        selectdata: (defaultParams.query && defaultParams.query.selectdata) ? defaultParams.query.selectdata : ''
      }
    },

    /**
     * 删除指定页面缓存
     * @param key 页面缓存key
     */
    removePageCache({ commit }, key) {
      commit('REMOVE_PAGE_CACHE', key)
    },

    /**
     * 清除所有页面缓存
     */
    clearAllPageCache({ commit }) {
      commit('CLEAR_ALL_PAGE_CACHE')
    }
  },
  getters: {
    historyRoutes: state => state.historyRoutes,
    getPageCache: state => key => state.pageCache[key] || null
  }
}
